import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { api } from '../hooks/useAuth';
import {
  Upload, FileText, Users, Send, ChevronRight, ChevronLeft,
  Plus, Trash2, Loader2, CheckCircle, Building2
} from 'lucide-react';

const STEPS = ['Upload PDF', 'Document Info', 'Add Recipients', 'Review & Send'];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {STEPS.map((label, i) => (
        <React.Fragment key={i}>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              i < current ? 'bg-emerald-600 text-white' :
              i === current ? 'bg-blue-600 text-white ring-4 ring-blue-600/20' :
              'bg-slate-800 text-slate-500'
            }`}>
              {i < current ? <CheckCircle size={14} /> : i + 1}
            </div>
            <span className={`text-sm font-medium hidden sm:block ${i === current ? 'text-white' : 'text-slate-500'}`}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`flex-1 h-px max-w-12 ${i < current ? 'bg-emerald-600' : 'bg-slate-800'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function NewDocumentPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);

  // Form state
  const [file, setFile] = useState(null);
  const [docId, setDocId] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', client_id: '', sending_company_id: '',
    expiry_days: 14, message: ''
  });
  const [recipients, setRecipients] = useState([
    { name: '', email: '', role: 'signer', signing_order: 1 }
  ]);
  const [uploadProgress, setUploadProgress] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/companies'),
      api.get('/clients?limit=100')
    ]).then(([c, cl]) => {
      setCompanies(c.data || []);
      setClients(cl.data?.data || []);
    }).catch(() => {});
  }, []);

  // Step 0 — dropzone
  const onDrop = useCallback(acceptedFiles => {
    const f = acceptedFiles[0];
    if (f) {
      setFile(f);
      if (!form.title) setForm(prev => ({ ...prev, title: f.name.replace(/\.pdf$/i, '') }));
    }
  }, [form.title]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'application/pdf': ['.pdf'] }, maxFiles: 1
  });

  const uploadDocument = async () => {
    if (!file) return;
    setUploadProgress(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      const { data } = await api.post('/documents/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocId(data.id);
      setStep(3); // jump to review if already filled
    } catch (e) {
      alert(e.response?.data?.error || 'Upload failed');
    } finally {
      setUploadProgress(false);
    }
  };

  const addRecipient = () => setRecipients(r => [
    ...r, { name: '', email: '', role: 'signer', signing_order: r.length + 1 }
  ]);

  const removeRecipient = i => setRecipients(r => r.filter((_, idx) => idx !== i));

  const updateRecipient = (i, field, value) => setRecipients(r =>
    r.map((rec, idx) => idx === i ? { ...rec, [field]: value } : rec)
  );

  const handleSend = async () => {
    setLoading(true);
    try {
      let id = docId;
      // If not yet uploaded, upload first
      if (!id) {
        const fd = new FormData();
        fd.append('pdf', file);
        Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
        const { data } = await api.post('/documents/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        id = data.id;
        setDocId(id);
      }

      // Add recipients
      await api.post(`/documents/${id}/recipients`, { recipients });

      // Send
      await api.post(`/documents/${id}/send`);
      navigate(`/documents/${id}`);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to send');
    } finally {
      setLoading(false);
    }
  };

  const canAdvance = () => {
    if (step === 0) return !!file;
    if (step === 1) return form.title && form.sending_company_id;
    if (step === 2) return recipients.every(r => r.name && r.email);
    return true;
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white tracking-tight">Send Document</h1>
        <p className="text-slate-500 text-sm mt-1">Send a PDF for signing in a few steps</p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
        <StepIndicator current={step} />

        {/* Step 0: Upload */}
        {step === 0 && (
          <div>
            <h2 className="text-lg font-semibold text-white mb-4">Upload PDF</h2>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                isDragActive
                  ? 'border-blue-500 bg-blue-500/5'
                  : file
                  ? 'border-emerald-500 bg-emerald-500/5'
                  : 'border-slate-700 hover:border-slate-600'
              }`}
            >
              <input {...getInputProps()} />
              {file ? (
                <>
                  <FileText size={40} className="mx-auto text-emerald-400 mb-3" />
                  <p className="font-semibold text-white">{file.name}</p>
                  <p className="text-sm text-slate-500 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  <p className="text-xs text-emerald-400 mt-2">Click to replace</p>
                </>
              ) : (
                <>
                  <Upload size={40} className="mx-auto text-slate-500 mb-3" />
                  <p className="font-semibold text-white">Drop your PDF here</p>
                  <p className="text-sm text-slate-500 mt-1">or click to browse</p>
                  <p className="text-xs text-slate-600 mt-3">PDF only · Max 50MB</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 1: Document Info */}
        {step === 1 && (
          <div className="space-y-5">
            <h2 className="text-lg font-semibold text-white mb-4">Document Details</h2>

            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Document Title *
              </label>
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Recruitment Agreement - Acme Corp"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Sending Company *
                </label>
                <select
                  value={form.sending_company_id}
                  onChange={e => setForm(f => ({ ...f, sending_company_id: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors"
                >
                  <option value="">Select entity...</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Client
                </label>
                <select
                  value={form.client_id}
                  onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors"
                >
                  <option value="">Select client...</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.company_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Expires After (days)
                </label>
                <input
                  type="number"
                  min="1" max="90"
                  value={form.expiry_days}
                  onChange={e => setForm(f => ({ ...f, expiry_days: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Message to Recipients (optional)
              </label>
              <textarea
                rows={3}
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                placeholder="Please review and sign this agreement at your earliest convenience..."
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors resize-none"
              />
            </div>
          </div>
        )}

        {/* Step 2: Recipients */}
        {step === 2 && (
          <div>
            <h2 className="text-lg font-semibold text-white mb-4">Add Recipients</h2>
            <div className="space-y-3">
              {recipients.map((r, i) => (
                <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Recipient {i + 1}
                    </span>
                    {recipients.length > 1 && (
                      <button onClick={() => removeRecipient(i)} className="text-red-400 hover:text-red-300 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      placeholder="Full Name *"
                      value={r.name}
                      onChange={e => updateRecipient(i, 'name', e.target.value)}
                      className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                    />
                    <input
                      placeholder="Email Address *"
                      type="email"
                      value={r.email}
                      onChange={e => updateRecipient(i, 'email', e.target.value)}
                      className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                    />
                    <select
                      value={r.role}
                      onChange={e => updateRecipient(i, 'role', e.target.value)}
                      className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors"
                    >
                      <option value="signer">Signer</option>
                      <option value="cc">CC (Observer)</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Signing order"
                      value={r.signing_order}
                      onChange={e => updateRecipient(i, 'signing_order', e.target.value)}
                      className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={addRecipient}
              className="mt-3 flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
            >
              <Plus size={15} /> Add another recipient
            </button>
          </div>
        )}

        {/* Step 3: Review & Send */}
        {step === 3 && (
          <div className="space-y-5">
            <h2 className="text-lg font-semibold text-white mb-4">Review & Send</h2>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Document</span>
                <span className="text-white font-medium">{form.title || file?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Sending as</span>
                <span className="text-white">{companies.find(c => c.id === form.sending_company_id)?.name || '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Client</span>
                <span className="text-white">{clients.find(c => c.id === form.client_id)?.company_name || '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Expires</span>
                <span className="text-white">In {form.expiry_days} days</span>
              </div>
              <div className="border-t border-slate-700 pt-3">
                <span className="text-slate-400 text-sm block mb-2">Recipients</span>
                {recipients.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm py-1">
                    <Users size={13} className="text-slate-500" />
                    <span className="text-white">{r.name}</span>
                    <span className="text-slate-500">&lt;{r.email}&gt;</span>
                    <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${
                      r.role === 'signer' ? 'bg-blue-500/15 text-blue-400' : 'bg-slate-700 text-slate-400'
                    }`}>{r.role}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-sm text-blue-300">
              Each signer will receive a secure email with a unique signing link. You'll be notified when they sign.
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-slate-800">
          <button
            onClick={() => step > 0 ? setStep(s => s - 1) : navigate(-1)}
            className="flex items-center gap-2 text-slate-400 hover:text-white text-sm font-medium transition-colors px-4 py-2 rounded-lg hover:bg-slate-800"
          >
            <ChevronLeft size={16} /> {step === 0 ? 'Cancel' : 'Back'}
          </button>

          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={!canAdvance()}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
            >
              Continue <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={loading || !canAdvance()}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {loading ? 'Sending...' : 'Send for Signature'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
